import { z } from 'zod';
import {
  AttachmentSchema,
  BaseRequestSchema,
  BodyHtmlSchema,
  BodyTextSchema,
  EmailDisplayNameSchema,
  MailAddressSchema,
  ProviderMessageIdSchema,
  SubjectSchema,
  UuidSchema,
} from './primitives.ts';

// deno-lint-ignore no-control-regex
// eslint-disable-next-line no-control-regex
const DISALLOWED_REFERENCES_CHARS = /[\x00-\x1F\x7F\u2028\u2029\uFEFF]/;

const RECIPIENT_LIST_MAX = 500;
const ATTACHMENT_LIST_MAX = 25;

/**
 * Identifiers a delegate's reply carries when it is sent from their own
 * personal mailbox on behalf of a shared inbox. Every field is a selector
 * consumed by `public.create_on_behalf_send_attempt` — that `SECURITY
 * DEFINER` RPC derives the actor and organization from `auth.uid()` and
 * re-validates every relationship, so nothing here is an authorization
 * input. The sending personal account is the request's own `accountId`;
 * the payload hash and recovery envelope are derived server-side from the
 * post-citation-strip wire payload, never supplied by the client.
 *
 * On-behalf intent is expressed ONLY by the presence of this bundle. It is
 * never inferred from the sending account diverging from the
 * correspondence's account — a legitimately migrated account would
 * otherwise be mislabelled.
 *
 * **`attemptId` and `deliveryProbeToken` MUST be stable across HTTP
 * retries of the same logical send.** The RPC is idempotent on
 * `attemptId` with a full field-equality re-check; a freshly generated id
 * per retry creates a second attempt and can deliver the same reply twice.
 * A caller retrying a timed-out or 5xx send MUST replay the identical
 * values it first sent, and MUST mint new ones only for a genuinely new
 * send.
 *
 * **`deliveryProbeToken` is also echoed inside the canonical payload.** The
 * RPC rejects with `22023` when
 * `p_payload #>> '{wire,deliveryProbeToken}'` differs from the
 * `p_delivery_probe_token` argument, so the caller that builds the payload
 * MUST write this same value at `payload.wire.deliveryProbeToken`.
 *
 * **The canonical payload MUST be a pure function of the parsed request.**
 * On replay the RPC compares the stored `p_payload` and `p_payload_sha256`
 * against the incoming pair by strict equality. Any non-deterministic
 * input — a timestamp, a freshly generated Message-ID, a citation strip
 * that is not idempotent — makes a legitimate retry differ from the stored
 * attempt, which raises `P0004` and wedges the send permanently at 409.
 *
 * Cross-field invariants involving this bundle live on the root union's
 * `superRefine` in `_shared/email/contracts.ts` — transition rule 4 keeps
 * per-action schemas a pure `ZodObject`.
 *
 * `.strict()`: `payloadSha256`, the bundle-level `organizationId`, and the
 * actor are all derived server-side. Silently stripping a client-supplied
 * copy would let a caller believe it parameterised a send it did not, so
 * the bundle rejects unknown keys instead.
 */
export const OnBehalfSendSchema = z
  .object({
    /** Idempotency key for the durable send attempt. Stable across retries. */
    attemptId: UuidSchema.brand<'OnBehalfAttemptId'>(),
    correspondenceId: UuidSchema.brand<'CorrespondenceId'>(),
    /** `email_drafts.id` the wire payload was rendered from. */
    sourceDraftId: UuidSchema.brand<'EmailDraftId'>(),
    /** `email_metadata.id` of the constituent message being replied to. */
    parentEmailMetadataId: UuidSchema.brand<'EmailMetadataId'>(),
    /** Origin shared account the reply is sent on behalf of. */
    onBehalfOfAccountId: UuidSchema.brand<'EmailAccountId'>(),
    /** Outbound delivery-probe correlator. Stable across retries. */
    deliveryProbeToken: UuidSchema,
  })
  .strict();

/**
 * Named because the branded members make the structural type impractical to
 * write by hand at a call site — a consumer threading the bundle into RPC
 * parameters needs a nameable parameter type.
 */
export type OnBehalfSend = z.infer<typeof OnBehalfSendSchema>;

/**
 * `public.outbound_send_attempt_status` — the durable lifecycle of one
 * on-behalf send attempt. Mirrors the enum declared in
 * `20260724064618_add_on_behalf_send_foundation.sql`; members are ordered
 * as declared there.
 */
export type OutboundSendAttemptStatus =
  | 'authorized'
  | 'dispatching'
  | 'provider_failed'
  | 'delivery_unknown'
  | 'provider_sent'
  | 'persisted'
  | 'persistence_failed';

/**
 * Row returned by `public.create_on_behalf_send_attempt`
 * (`RETURNS TABLE (attempt_id, attempt_status, created_at)`).
 *
 * Keys are snake_case because this is the PostgREST row verbatim — the RPC
 * boundary performs no renaming, and a camelCase mirror would read
 * `undefined` at runtime while type-checking clean.
 *
 * `attempt_status` is load-bearing on the idempotent-replay path: replaying
 * an attempt that already reached `provider_sent` or `persisted` still
 * returns success here, and only the subsequent compare-and-set claim
 * fails. A caller that ignores this field discovers the replay as a 409 and
 * reports a send that actually SUCCEEDED as a conflict — it MUST branch on
 * the returned status before attempting to claim.
 */
export interface OnBehalfAttemptResult {
  readonly attempt_id: string;
  readonly attempt_status: OutboundSendAttemptStatus;
  readonly created_at: string;
}

export const SendEmailSchema = BaseRequestSchema.extend({
  action: z.literal('send_email'),
  accountId: UuidSchema,
  to: z.array(MailAddressSchema).min(1).max(RECIPIENT_LIST_MAX),
  cc: z.array(MailAddressSchema).max(RECIPIENT_LIST_MAX).default([]),
  bcc: z.array(MailAddressSchema).max(RECIPIENT_LIST_MAX).default([]),
  subject: SubjectSchema,
  bodyHtml: BodyHtmlSchema,
  bodyText: BodyTextSchema.optional(),
  replyToMessageId: ProviderMessageIdSchema.optional(),
  threadId: ProviderMessageIdSchema.optional(),
  /**
   * RFC 5322 References header value — space-separated chain of Message-IDs
   * leading to the parent. Required for downstream-client threading depth
   * beyond 2 levels; without it, recipient mail clients stop grouping replies
   * after the first generation. Same disallowed-char set as
   * `ProviderMessageIdSchema` — header-injection vector if any control or
   * line-separator character lands in an outbound RFC 5322 header.
   */
  references: z
    .string()
    .max(8 * 1024)
    .refine((value) => !DISALLOWED_REFERENCES_CHARS.test(value), {
      message: 'references must not contain control or line-separator characters',
    })
    .optional(),
  /**
   * The correspondence the message is composed in, when the caller knows it.
   *
   * Linkage only, and only AFTER the send path verifies it belongs to the
   * sending account's organization — `body` fields are untrusted input and are
   * never an authorization decision (AGENTS.md → "Cross-tenant isolation").
   * The verified value reaches adapters as `SendAdapterContext.
   * verifiedCorrespondenceId`, never as this raw field.
   *
   * Optional because the compose path does not always have one (a brand-new
   * conversation has no correspondence until sync mints it) and because every
   * pre-existing client omits it.
   */
  correspondenceId: UuidSchema.optional(),
  /** Public Pulse draft to complete after provider acceptance. Never a provider draft ID. */
  sourceDraftId: UuidSchema.optional(),
  fromAlias: MailAddressSchema.optional(),
  /**
   * Optional display name for the `From:` header (e.g. `"Office Name"`).
   * The adapter validator overrides this with the DB's `display_name` for
   * the resolved alias when the client omits it — so most callers don't
   * need to send it. When provided, it survives schema hardening at the
   * boundary (control chars, quote/backslash, U+2028/2029/FEFF rejected).
   */
  fromName: EmailDisplayNameSchema,
  attachments: z.array(AttachmentSchema).max(ATTACHMENT_LIST_MAX).default([]),
  onBehalf: OnBehalfSendSchema.optional(),
});

export const SaveDraftSchema = BaseRequestSchema.extend({
  action: z.literal('save_draft'),
  mode: z.enum(['new', 'reply', 'replyAll', 'forward']).default('new'),
  /**
   * For reply/replyAll/forward: the message being replied to. Provides the
   * MIME In-Reply-To + References headers. NOT the same as Gmail's
   * thread-id (pass `threadId` separately).
   */
  referenceMessageId: ProviderMessageIdSchema.optional(),
  /**
   * Gmail thread-id for draft threading. Required by Gmail's `/drafts` API
   * to attach a draft to an existing thread; Outlook drafts don't use this.
   */
  threadId: ProviderMessageIdSchema.optional(),
  to: z.array(MailAddressSchema).default([]),
  cc: z.array(MailAddressSchema).default([]),
  bcc: z.array(MailAddressSchema).default([]),
  subject: SubjectSchema.default(''),
  bodyHtml: BodyHtmlSchema.default(''),
});

export const UpdateDraftSchema = BaseRequestSchema.extend({
  action: z.literal('update_draft'),
  draftId: ProviderMessageIdSchema,
  etag: z.string().optional(),
  referenceMessageId: ProviderMessageIdSchema.optional(),
  threadId: ProviderMessageIdSchema.optional(),
  mode: z.enum(['new', 'reply', 'replyAll', 'forward']).optional(),
  to: z.array(MailAddressSchema).optional(),
  cc: z.array(MailAddressSchema).optional(),
  bcc: z.array(MailAddressSchema).optional(),
  subject: SubjectSchema.optional(),
  bodyHtml: BodyHtmlSchema.optional(),
});

export const DeleteDraftSchema = BaseRequestSchema.extend({
  action: z.literal('delete_draft'),
  draftId: ProviderMessageIdSchema,
});
